import { useState } from "react";
import { PolicyAcquisitionHeader } from "./components/PolicyAcquisitionHeader";
import { PolicyAcquisitionDialog } from "./components/PolicyAcquisitionDialog";
import { PolicyAcquisitionTable } from "./components/PolicyAcquisitionTable";
import { DeleteConfirmationDialog } from "./components/DeleteConfirmationDialog";
import { usePolicyAcquisitionTypes } from "./hooks/usePolicyAcquisitionTypes";
import { usePolicyAcquisitionOperations } from "./hooks/usePolicyAcquisitionOperations";

interface FormData {
  name: string;
  description: string;
  sort_order?: number;
}

export const PolicyAcquisitionContainer = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<any>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    description: "",
  });

  const { data: policyAcquisitionTypes, isLoading, refetch } = usePolicyAcquisitionTypes();
  const { createPolicyAcquisition, updatePolicyAcquisition, deletePolicyAcquisition } = 
    usePolicyAcquisitionOperations();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let success;

    if (selectedType) {
      success = await updatePolicyAcquisition(selectedType.id, formData);
    } else {
      success = await createPolicyAcquisition(formData);
    }

    if (success) {
      setIsDialogOpen(false);
      setSelectedType(null);
      setFormData({ name: "", description: "" });
      // Immediately refetch the data after successful creation/update
      await refetch();
    }
  };

  const handleEdit = (type: any) => {
    setSelectedType(type);
    setFormData({
      name: type.name,
      description: type.description || "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (deletingId) {
      const success = await deletePolicyAcquisition(deletingId);
      if (success) {
        setIsDeleteDialogOpen(false);
        setDeletingId(null);
        // Immediately refetch the data after successful deletion
        await refetch();
      }
    }
  };

  const handleReorder = async (reorderedItems: any[]) => {
    const updatedItems = reorderedItems.map((item, index) => ({
      ...item,
      sort_order: index + 1,
    }));
    
    // Update the order in the database
    for (const item of updatedItems) {
      await updatePolicyAcquisition(item.id, {
        name: item.name,
        description: item.description,
        sort_order: item.sort_order,
      } as FormData);
    }
    // Refetch after reordering is complete
    await refetch();
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <PolicyAcquisitionHeader
        onAddClick={() => {
          setSelectedType(null);
          setFormData({ name: "", description: "" });
          setIsDialogOpen(true);
        }}
      />

      <PolicyAcquisitionTable
        policyAcquisitionTypes={policyAcquisitionTypes || []}
        onEdit={handleEdit}
        onDelete={(id) => {
          setDeletingId(id);
          setIsDeleteDialogOpen(true);
        }}
        onReorder={handleReorder}
      />

      <PolicyAcquisitionDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        selectedType={selectedType}
        formData={formData}
        onFormChange={setFormData}
        onSubmit={handleSubmit}
      />

      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};